import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { getUberBranches, getUberMenuPreview, getUberLiveMenu, triggerUberSync } from "../../api/Products";
import UberSyncUI from "./components/UberSyncUI";

const Main = () => {
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // States for Preview Panel
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMenu, setPreviewMenu] = useState(null);
  const [previewExclusions, setPreviewExclusions] = useState([]);
  const [previewDiscrepancies, setPreviewDiscrepancies] = useState([]);
  const [previewRawPayload, setPreviewRawPayload] = useState(null);

  // States for Live Menu from Uber
  const [liveMenuLoading, setLiveMenuLoading] = useState(false);
  const [liveMenu, setLiveMenu] = useState(null);
  const [liveRawPayload, setLiveRawPayload] = useState(null);

  // States for Sincronización Modal
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResults, setSyncResults] = useState([]);

  // Última sincronización
  const [lastSyncTime, setLastSyncTime] = useState(null);

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUberBranches();
      if (response && response.data && response.data.status === "success") {
        const branchList = response.data.branches || [];
        setBranches(branchList);
        if (response.data.lastSyncTime) setLastSyncTime(response.data.lastSyncTime);
        
        // Cargar previsualización del menú global (backend selecciona automáticamente la primera activa)
        fetchGlobalPreview();
      } else {
        message.error("Error al cargar las sucursales habilitadas para Uber Eats");
      }
    } catch (err) {
      console.error("Error fetching Uber branches:", err);
      message.error("Error al cargar las sucursales habilitadas para Uber Eats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const fetchGlobalPreview = async () => {
    setPreviewLoading(true);
    setPreviewMenu(null);
    setPreviewExclusions([]);
    setLiveMenuLoading(true);
    setLiveMenu(null);

    const fetchLocal = async () => {
      try {
        const response = await getUberMenuPreview();
        if (response && response.data && response.data.status === "success") {
          setPreviewMenu(response.data.menu || []);
          setPreviewExclusions(response.data.exclusions || []);
          setPreviewDiscrepancies(response.data.discrepancies || []);
          setPreviewRawPayload(response.data.rawPayload || null);
        } else {
          message.error(response.data?.message || "Error al cargar la previsualización del menú");
        }
      } catch (err) {
        console.error("Error fetching preview menu:", err);
        message.error("Error al cargar la previsualización del menú");
      } finally {
        setPreviewLoading(false);
      }
    };

    const fetchLive = async () => {
      try {
        const response = await getUberLiveMenu();
        if (response && response.data && response.data.status === "success") {
          setLiveMenu(response.data.menu || []);
          setLiveRawPayload(response.data.rawPayload || null);
        } else {
          console.warn("Uber Live Menu fetch warning:", response.data?.message);
          setLiveMenu([]);
        }
      } catch (err) {
        console.warn("Error fetching Uber live menu:", err);
        setLiveMenu([]);
      } finally {
        setLiveMenuLoading(false);
      }
    };

    Promise.all([fetchLocal(), fetchLive()]);
  };

  const handleSelectRowKeysChange = useCallback((keys) => {
    setSelectedRowKeys(keys);
  }, []);

  const handleTriggerSync = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;

    // Inicializar resultados en cola/pendiente
    const initialResults = selectedRowKeys.map((id) => {
      const branch = branches.find((b) => b.id === id);
      return {
        branchId: id,
        name: branch ? branch.name : `Sucursal #${id}`,
        status: "pending",
        message: ""
      };
    });

    setSyncResults(initialResults);
    setSyncModalVisible(true);
    setSyncLoading(true);

    try {
      // Mostrar estado "syncing" para todos los ítems de la cola en el modal
      setSyncResults(prev => prev.map(item => ({ ...item, status: "syncing" })));

      // Disparar API de sincronización a backend
      const response = await triggerUberSync(selectedRowKeys);

      if (response && response.data && response.data.status === "success") {
        const results = response.data.results || [];
        
        // Mapear los resultados finales del backend en el modal
        setSyncResults(prev =>
          prev.map((item) => {
            const apiRes = results.find((r) => r.branchId === item.branchId);
            return {
              ...item,
              status: apiRes ? apiRes.status : "error",
              message: apiRes ? apiRes.message : "Sin respuesta del servidor"
            };
          })
        );
        message.success("Proceso de sincronización completado");
      } else {
        setSyncResults(prev =>
          prev.map(item => ({
            ...item,
            status: "error",
            message: response.data?.message || "Error inesperado del servidor"
          }))
        );
        message.error("Error al sincronizar las sucursales");
      }
    } catch (err) {
      console.error("Error synchronizing Uber menus:", err);
      setSyncResults(prev =>
        prev.map(item => ({
          ...item,
          status: "error",
          message: "Ocurrió un error de red o de comunicación con el servidor"
        }))
      );
      message.error("Error al sincronizar las sucursales");
    } finally {
      setSyncLoading(false);
      // Limpiar selección de filas tras sincronización exitosa/finalizada
      setSelectedRowKeys([]);
    }
  }, [selectedRowKeys, branches]);

  const handleCloseSyncModal = useCallback(() => {
    setSyncModalVisible(false);
    setSyncResults([]);
  }, []);

  return (
    <UberSyncUI
      loading={loading}
      branches={branches}
      selectedRowKeys={selectedRowKeys}
      onSelectRowKeysChange={handleSelectRowKeysChange}
      previewLoading={previewLoading}
      previewMenu={previewMenu}
      previewExclusions={previewExclusions}
      previewDiscrepancies={previewDiscrepancies}
      previewRawPayload={previewRawPayload}
      liveMenuLoading={liveMenuLoading}
      liveMenu={liveMenu}
      liveRawPayload={liveRawPayload}
      syncModalVisible={syncModalVisible}
      syncLoading={syncLoading}
      syncResults={syncResults}
      onTriggerSync={handleTriggerSync}
      onCloseSyncModal={handleCloseSyncModal}
      lastSyncTime={lastSyncTime}
    />
  );
};

export default Main;
